/**
 * Valida el RUT uruguayo (12 dígitos - RUC).
 * Algoritmo según DGI Uruguay.
 *
 * 1) Se toman los primeros 11 dígitos
 * 2) Se multiplica cada dígito por factores (izquierda a derecha): 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2
 * 3) Se suman los productos
 * 4) Suma / 11. Probable verificador = 11 - resto
 *    - Si < 10: es el dígito verificador
 *    - Si 11: verificador es 0
 *    - Si 10: RUT inválido (número desechado)
 * 5) Pos 1-2: rango 01 a 22 | Pos 3-8: distinto de 0 | Pos 9-10: deben ser 0
 */
export function validateRutUY(rut, { required = false } = {}) {
  // BUG-81 fix: Si el campo es requerido, retornar inválido para vacío/null
  if (!rut || typeof rut !== 'string') {
    return required
      ? { valid: false, message: 'El RUT es obligatorio' }
      : { valid: true, message: null };
  }

  const cleaned = rut.replace(/[\s.-]/g, '').replace(/\D/g, '');
  if (cleaned.length === 0) {
    return required
      ? { valid: false, message: 'El RUT es obligatorio' }
      : { valid: true, message: null };
  }

  if (cleaned.length !== 12) {
    return { valid: false, message: 'El RUT debe tener 12 dígitos (sin puntos ni guiones)' };
  }

  const first11 = cleaned.slice(0, 11);
  const digVerificador = parseInt(cleaned.charAt(11), 10);

  // Factores: 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2 (primer dígito × 4, segundo × 3, etc.)
  const factors = [4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

  let sum = 0;
  for (let i = 0; i < 11; i++) {
    sum += parseInt(first11.charAt(i), 10) * factors[i];
  }

  const resto = sum % 11;
  const probableCheckDigit = 11 - resto;

  let expectedVerificador;
  if (probableCheckDigit === 10) {
    return { valid: false, message: 'RUT inválido. Verifique el número ingresado' };
  }
  if (probableCheckDigit === 11) {
    expectedVerificador = 0;
  } else {
    expectedVerificador = probableCheckDigit;
  }

  if (digVerificador !== expectedVerificador) {
    return { valid: false, message: 'RUT inválido. El dígito verificador no coincide' };
  }

  // Posiciones 1-2: rango 01 a 22
  const pos1_2 = parseInt(cleaned.slice(0, 2), 10);
  if (pos1_2 < 1 || pos1_2 > 22) {
    return { valid: false, message: 'RUT inválido. Las dos primeras posiciones deben estar entre 01 y 22' };
  }

  // Posiciones 3-8: distinto de 000000
  if (cleaned.slice(2, 8) === '000000') {
    return { valid: false, message: 'RUT inválido. Las posiciones 3 a 8 no pueden ser 000000' };
  }

  // Posiciones 9-10: deben ser 00
  if (cleaned.slice(8, 10) !== '00') {
    return { valid: false, message: 'RUT inválido. Las posiciones 9 y 10 deben ser 00' };
  }

  return { valid: true, message: null };
}
